import { MouseEvent, useMemo } from 'react';

import { LaneDirection, LocationInput } from '@kinetic-ui/data-layer';
import { LaneDragPoints } from './LaneDragPoints';
import { useSelectedLane } from '../selectedLaneState';
import { RecursivePartial } from 'types';
import { MapSVGOverlay } from 'components/locations/MapSVGOverlay';
import Lane from 'components/Lane';

type EditMapLanesProps = {
  location: RecursivePartial<LocationInput>;
};

export default function EditMapLanes({ location }: EditMapLanesProps) {
  const { selectedLane, setSelectedLane } = useSelectedLane();

  const otherLanes = useMemo(() => {
    if (!location?.lanes) {
      return [];
    }
    return selectedLane
      ? location.lanes.filter((lane) => selectedLane.id !== lane.id)
      : location.lanes;
  }, [selectedLane, location.lanes]);

  const onClickLane = (
    _e: MouseEvent<SVGLineElement>,
    segmentIndex: number,
    { lat, lng }: { lat: number; lng: number }
  ) => {
    const insertionIndex = segmentIndex + 1;

    setSelectedLane((prev) => {
      const newCoordinates = prev?.geometry?.coordinates ? [...prev.geometry.coordinates] : [];
      // insert new point into the coordinates
      newCoordinates.splice(insertionIndex, 0, [lng, lat]);
      return {
        ...prev,
        geometry: { type: 'LineString', coordinates: newCoordinates },
      };
    });
  };

  return (
    <MapSVGOverlay>
      <g>
        {selectedLane && (
          <Lane
            coordinates={location?.geometry?.coordinates}
            lane={selectedLane}
            selected
            onClickLane={onClickLane}
            departure={selectedLane?.laneDirection === LaneDirection.Departure}
          />
        )}
        {otherLanes.map((lane) => (
          <Lane
            coordinates={location?.geometry?.coordinates}
            key={lane.id}
            connectable={false}
            departure={lane.laneDirection === LaneDirection.Departure}
            lane={lane}
            onClickLane={() => {
              setSelectedLane(lane);
            }}
            onClickConnect={() => null}
          />
        ))}
      </g>
      <LaneDragPoints editingLane={selectedLane} setEditingLane={setSelectedLane} />
    </MapSVGOverlay>
  );
}
